import { useListener } from '#~/command/hooks.js';
import { Command } from '#~/command/index.js';
import { useSelectedVideo } from '#~/room/hooks.js';
import { Controls } from '../controls/index.js';
import { Image } from '../image/index.js';
import { Presets } from '../image/presets/index.js';
import { Styles } from './constants.js';
import { Drawer } from './drawer.jsx';
import { useRightDrawerPref, useRightTabPref } from './hooks.js';
import { Root } from './root.jsx';
import { Triggers } from './triggers.jsx';
import type { Props } from './types.js';
import { Tab } from './types.js';
import { AnimatePresence, motion, Motions } from '@studio/design-system/motion';
import { Content } from '@studio/design-system/tabs';
import { toAccessor } from '@studio/react-utils';
import type { ReactElement } from 'react';
import { twMerge } from 'cn';

/** `<RightTray>` component. */
export const RightTray = ({ className, ...props }: Props): ReactElement | null => {
  const [tab, setTab] = useRightTabPref();
  const drawer = toAccessor(useRightDrawerPref());
  const [deviceId] = useSelectedVideo();

  // Command listener.
  useListener(({ command }) => {
    if (command === Command.TRAY_RIGHT_TAB) {
      setTab(tab === Tab.Controls ? Tab.Image : Tab.Controls);
    }
  });

  const passthru = {
    className: 'overflow-y-auto overflow-x-hidden',
    deviceId
  };

  return (
    <>
      <Drawer
        className={twMerge('absolute inset-0 z-[35]', Styles.root, !drawer.value && 'shadow-none')}
        {...{ 'data-state': drawer.value && tab === Tab.Image ? 'open' : 'closed' }}
      >
        <AnimatePresence>
          {!!drawer.value && tab === Tab.Image && (
            <motion.div className='h-full' {...Motions.Fade}>
              <Presets deviceId={deviceId} drawer={drawer} />
            </motion.div>
          )}
        </AnimatePresence>
      </Drawer>

      <Root
        className={twMerge('relative z-40', className)}
        tab={toAccessor([tab, setTab])}
        {...props}
      >
        <Triggers />

        <Content value={Tab.Controls}>{tab === Tab.Controls && <Controls {...passthru} />}</Content>

        <Content value={Tab.Image}>
          {tab === Tab.Image && <Image drawer={drawer} {...passthru} />}
        </Content>
      </Root>
    </>
  );
};
